import type { Entry, Fixture } from '@h2h/shared';
import type { DuelSide } from '@h2h/shared';
import { cn } from '../../lib/cn';
import { ArrowRight } from '../../components/ui/icons';
import { DuelPickCard } from '../../components/play/DuelPickCard';
import { StakeLadder, type LadderRungData } from '../../components/play/cycling-ui';
import { CYCLING_WEIGHTS } from '../../lib/cyclingPicks';
import { TOTAL } from './constants';

export function CyclingCallsStep({
  slate,
  sideByDuel,
  frozen,
  pickedCount,
  onPick,
  onContinue,
  pinBottom = true
}: {
  slate: Fixture[];
  sideByDuel: Record<string, DuelSide | undefined>;
  frozen: boolean;
  pickedCount: number;
  onPick: (duelId: string, side: DuelSide) => void;
  onContinue: () => void;
  /** Sticky footer — off in the league-hub pick sheet (scrolls with content). */
  pinBottom?: boolean;
}) {
  const allPicked = pickedCount === TOTAL;
  const remaining = TOTAL - pickedCount;
  const firstOpenIdx = slate.findIndex((f) => !sideByDuel[f.fixtureId]);

  return (
    <>
      <div className="flex flex-col gap-[11px] pb-4 pt-4">
        {slate.map((f, i) => (
          <DuelPickCard
            key={f.fixtureId}
            id={`duel-card-${f.fixtureId}`}
            fixture={f}
            index={i}
            pick={sideByDuel[f.fixtureId]}
            active={!frozen && i === firstOpenIdx}
            disabled={frozen}
            onPick={(side) => onPick(f.fixtureId, side)}
          />
        ))}
      </div>

      <CyclingCallsFooter
        frozen={frozen}
        allPicked={allPicked}
        pickedCount={pickedCount}
        remaining={remaining}
        onContinue={onContinue}
        pinBottom={pinBottom}
      />
    </>
  );
}

export function CyclingRankStep({
  rungs,
  frozen,
  onReorder
}: {
  rungs: LadderRungData[];
  frozen: boolean;
  onReorder: (next: LadderRungData[]) => void;
}) {
  return (
    <div className="pb-4 pt-4">
      <StakeLadder
        rungs={rungs}
        weights={[...CYCLING_WEIGHTS]}
        disabled={frozen}
        onReorder={onReorder}
        footerVariant="reorder"
      />
    </div>
  );
}

function CyclingCallsFooter({
  frozen,
  allPicked,
  pickedCount,
  remaining,
  onContinue,
  pinBottom = true
}: {
  frozen: boolean;
  allPicked: boolean;
  pickedCount: number;
  remaining: number;
  onContinue: () => void;
  pinBottom?: boolean;
}) {
  return (
    <div className={pinBottom ? 'sticky bottom-0 z-30' : 'mt-3'}>
      {pinBottom && (
        <div className="pointer-events-none absolute inset-x-0 -top-7 h-7 bg-gradient-to-t from-white to-transparent" />
      )}
      <div className={cn('bg-white pt-3.5', pinBottom ? 'pb-[max(12px,var(--sab))]' : 'pb-4')}>
        {frozen ? (
          <p className="text-center text-[11px] font-semibold text-ink2">Locked in — your picks are saved.</p>
        ) : allPicked ? (
          <>
            <button
              type="button"
              onClick={onContinue}
              className="tap flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] p-[15px] shadow-[0_18px_34px_-16px_rgba(11,160,120,0.7)]"
            >
              <span className="text-[15px] font-extrabold tracking-[-0.01em] text-white">Rank your calls</span>
              <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
            </button>
            <p className="mt-2 text-center text-[11px] font-semibold text-ink3">All six called · set your confidence ladder next</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 rounded-2xl bg-[#EDEDF1] p-[15px]">
              <span className="text-[15px] font-extrabold tracking-[-0.01em] text-[#B0B0B8]">
                {remaining === TOTAL ? 'Rank your calls' : `Call ${remaining} more to rank`}
              </span>
              <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-[#B0B0B8]" />
            </div>
            <p className="mt-2 text-center text-[11px] font-semibold text-ink3">
              {remaining === TOTAL ? 'Call all 6 duels to continue' : `${pickedCount} of ${TOTAL} called · edit any before the stage rolls out`}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function CyclingRankFooter({
  frozen,
  valid,
  error,
  isPending,
  onLock,
  doneLabel = 'All set',
  pinBottom = true
}: {
  frozen: boolean;
  valid: boolean;
  error: string | null;
  isPending: boolean;
  onLock: () => void;
  doneLabel?: string;
  pinBottom?: boolean;
}) {
  return (
    <div className={pinBottom ? 'sticky bottom-0 z-30' : 'mt-3'}>
      {pinBottom && (
        <div className="pointer-events-none absolute inset-x-0 -top-7 h-7 bg-gradient-to-t from-white to-transparent" />
      )}
      <div className={cn('bg-white pt-3', pinBottom ? 'pb-[max(14px,var(--sab))]' : 'pb-4')}>
        {error && (
          <p className="mb-3 rounded-xl bg-miss/10 px-3 py-2 text-[13px] font-semibold text-danger">{error}</p>
        )}
        {frozen ? (
          <p className="text-center text-[11px] font-semibold text-ink2">Your ladder is locked for this stage.</p>
        ) : (
          <>
            <button
              type="button"
              disabled={!valid || isPending}
              onClick={onLock}
              className="tap flex w-full items-center justify-center gap-2 rounded-[17px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] p-4 shadow-[0_18px_34px_-16px_rgba(11,160,120,0.7)] disabled:opacity-40"
            >
              <span className="text-[15px] font-extrabold tracking-[-0.01em] text-white">
                {isPending ? 'Saving…' : doneLabel}
              </span>
              <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
            </button>
            <p className="mt-2 text-center text-[11px] font-semibold text-ink3">
              Saved as you rank · re-order anytime before the stage rolls out
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** @deprecated Used by gate screens only. */
export function CyclingPicksFooter({
  frozen,
  allPicked,
  pickedCount,
  valid,
  confidenceConfirmed,
  hasEntry,
  error,
  isPending,
  onGoBoard,
  onCommit
}: {
  frozen: boolean;
  allPicked: boolean;
  pickedCount: number;
  valid: boolean;
  confidenceConfirmed: boolean;
  hasEntry: Entry | null | undefined;
  error: string | null;
  isPending: boolean;
  onGoBoard: () => void;
  onCommit: () => void;
}) {
  void confidenceConfirmed;
  void hasEntry;
  void onGoBoard;
  if (!allPicked) {
    return (
      <CyclingCallsFooter
        frozen={frozen}
        allPicked={false}
        pickedCount={pickedCount}
        remaining={TOTAL - pickedCount}
        onContinue={onCommit}
      />
    );
  }
  return (
    <CyclingRankFooter frozen={frozen} valid={valid} error={error} isPending={isPending} onLock={onCommit} />
  );
}
